import instance from "api/globalaxios";
import { getPlatform } from "slices/disputeSlice";

const MEDIATION_API_URL = "api/mediation-form-data/";

export const CONFLICT_FIELD_MAP = {
  // Claimant conflicts
  claimant_email: "claimant_email",
  claimant_mobile: "claimant_mobile",
  claimant_phone: "claimant_mobile",

  // Respondent conflicts
  respondent_email: "respondent_email",
  respondent_mobile: "respondent_mobile",
  respondent_phone: "respondent_mobile",
};

/**
 * Friendly messages for known conflict scenarios.
 */
export const CONFLICT_MESSAGES = {
  claimant_email:
    "This email address is already registered as a claimant.",
  claimant_mobile:
    "This mobile number is already registered as a claimant.",
  respondent_email:
    "This email address is already registered in our system.",
  respondent_mobile:
    "This mobile number is already registered in our system.",
};

/**
 * Submits the mediation request to the API.
 *
 * @param {object} values   - Formik values (flat key-value pairs)
 * @param {File[]} files    - Array of File objects from the document upload
 * @returns {Promise<{ success: boolean, referenceId?: string, fieldErrors?: object, globalError?: string }>}
 */
export const submitMediationRequest = async (values, files = []) => {
  try {
    const formData = new FormData();

    // Append all text/select/radio fields
    Object.entries(values).forEach(([key, val]) => {
      formData.append(key, val || "");
    });

    // Append uploaded files
    files.forEach((file) => {
      formData.append("documents", file);
    });

    const response = await instance(MEDIATION_API_URL, {
      method: "POST",
      data: formData,
      params: {
        host: getPlatform(),
      }
      // Do NOT set Content-Type — browser sets it with boundary for multipart
    });

    console.log('res', response)

    // ── Success ────────────────────────────────────────────────────────────
    if (response.status === 200) {
      const data = response.data || {};
      return {
        success: true,
        referenceId: data.referenceId || data.reference_id || null,
      };
    }

    // ── 409 Conflict — duplicate email / phone ─────────────────────────────
    if (response.status === 409) {
      const data = response.data || {};
      const fieldErrors = {};

      // API may return: { conflicts: ["claimant_email", "respondent_mobile"] }
      const conflicts = data.conflicts || data.fields || [];

      if (Array.isArray(conflicts) && conflicts.length > 0) {
        conflicts.forEach((conflictKey) => {
          const formField = CONFLICT_FIELD_MAP[conflictKey];
          if (formField) {
            fieldErrors[formField] =
              CONFLICT_MESSAGES[formField] ||
              "This value already exists in our records.";
          }
        });
      }

      // Fallback: API returns a single field key directly
      if (Object.keys(fieldErrors).length === 0 && data.field) {
        const formField = CONFLICT_FIELD_MAP[data.field];
        if (formField) {
          fieldErrors[formField] =
            CONFLICT_MESSAGES[formField] ||
            "This value already exists in our records.";
        }
      }

      if (Object.keys(fieldErrors).length > 0) {
        return { success: false, fieldErrors };
      }

      return {
        success: false,
        globalError:
          data.message ||
          "A record with the provided email or mobile number already exists. Please review your details.",
      };
    }

    // ── 400 Validation Error ───────────────────────────────────────────────
    if (response.status === 400) {
      const data = response.data || {};
      return {
        success: false,
        globalError:
          data?.response?.data?.globalError ||
          data?.message ||
          "Some fields could not be validated by the server. Please review your entries.",
      };
    }

    // ── Other server errors ────────────────────────────────────────────────
    return {
      success: false,
      globalError:
        "An unexpected error occurred while submitting your request. Please try again in a few moments.",
    };
  } catch (networkError) {
    console.error("[MediationAPI] Network error:", networkError);
    return {
      success: false,
      globalError:
        networkError?.response?.data?.globalError ||
        networkError?.message ||
        "Unable to reach the server. Please check your internet connection and try again.",
    };
  }
};
